
import moment from 'moment'
import numbro from 'numbro'
import { mapGetters, mapActions } from 'vuex'

import CreateBudgetSimulation from '@/components/budgetSimulator/CreateBudgetSimulation.vue'
import BudgetSimulationCard from '@/components/budgetSimulator/BudgetSimulationCard.vue'
import CreatePlanChooser from '@/components/CreatePlanChooser.vue'
import { currentModelReport } from '@/mixins/currentModelReport.js'
import { ngtorchRuns } from '@/mixins/ngtorchRuns.js'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import { lastYearWindow, nextYearWindow, parsedResults, historicalMix, normalizeMix } from '@/utils/budgetSimulator.js'
import { calculatorRange, planAxis, buildPrefillLookup, buildFlexibleMediaPlan, flexibleBounds as dataBounds } from '@/utils/flexibleMediaPlan.js'

export default {
  mixins: [currentModelReport, ngtorchRuns],
  components: {
    CreateBudgetSimulation,
    BudgetSimulationCard,
    CreatePlanChooser,
    NgtorchRunChip
  },
  data () {
    return {
      search: '',
      dialog: false,
      chooser: false,
      baseSimulation: null,
      edit: false,
      expanded: [],
      pendingExpand: null
    }
  },
  methods: {
    ...mapActions('simulations', ['loadSimulations']),
    ...mapActions('clients', ['setCurrentClient']),
    openDialog (base, edit) {
      this.baseSimulation = base
      this.edit = !!edit
      this.dialog = true
    },
    onCreateOption (id, period) {
      if (id === 'flexible') {
        this.createFlexibleDates(period)
      } else if (id === 'scratch') {
        this.openDialog(null)
      } else if (id === 'lastYearFree') {
        this.createLastYear('free')
      } else if (id === 'lastYearFrozen') {
        this.createLastYear('frozen')
      } else if (id === 'nextYearFree') {
        this.createNextYearFree()
      }
    },
    closeDialog () {
      this.dialog = false
      this.baseSimulation = null
      this.edit = false
    },
    createLastYear (mixMode) {
      if (!this.lastYear) return
      const key = mixMode === 'frozen' ? 'createLastYearFrozen' : 'createLastYearFree'
      this.openDialog({
        name: this.$t(`views.BudgetSimulator.${key}`),
        date_from: this.lastYear.date_from,
        date_to: this.lastYear.date_to,
        mix_mode: mixMode
      })
    },
    createNextYearFree () {
      if (!this.nextYear) return
      this.openDialog({
        name: this.$t('views.BudgetSimulator.createNextYearFree'),
        date_from: this.nextYear.date_from,
        date_to: this.nextYear.date_to,
        mix_mode: 'free',
        reference_budget: this.nextYear.budget
      })
    },
    // Dates of a period picked in the "Flexible Dates" step: months become their first and last
    // day, kept inside the dates with data (flexibleBounds).
    flexibleDates (period) {
      const range = this.flexibleBounds
      let from = period.from
      let to = period.to
      if (this.modelFrequency !== 'week') {
        from = from + '-01'
        to = moment(to + '-01').endOf('month').format('YYYY-MM-DD')
      }
      return {
        dateFrom: from < range.start ? range.start : from,
        dateTo: to > range.end ? range.end : to
      }
    },
    // Same per-period values as the Plan Calculator's Flexible Dates plan.
    flexiblePlan (period) {
      const dates = this.flexibleDates(period)
      const axis = planAxis(this.flexibleRange, dates.dateFrom, dates.dateTo, this.modelFrequency)
      return buildFlexibleMediaPlan(axis, Object.keys(this.channels), this.flexibleLookup, this.modelFrequency)
    },
    formatPeriodKey (key) {
      return this.modelFrequency === 'week' ? moment(key).format('ll') : moment(key + '-01').format('MMM YYYY')
    },
    flexibleSummary (period) {
      const plan = this.flexiblePlan(period)
      const unitKey = this.modelFrequency === 'week' ? 'views.BudgetSimulator.flexibleWeeks' : 'views.BudgetSimulator.flexibleMonths'
      const sources = [
        { count: plan.counts.actual, icon: 'mdi-history', color: 'primary', label: 'views.BudgetSimulator.flexibleSummaryActual' },
        { count: plan.counts.propagation, icon: 'mdi-calendar-arrow-right', color: 'primary', label: 'views.BudgetSimulator.flexibleSummaryPropagation' },
        { count: plan.counts.empty, icon: 'mdi-circle-outline', color: 'grey', label: 'views.BudgetSimulator.flexibleSummaryEmpty' }
      ]
      const rows = sources.filter(source => source.count > 0).map(source => ({
        icon: source.icon,
        color: source.color,
        label: this.$t(source.label),
        value: this.$tc(unitKey, source.count, { count: source.count })
      }))
      rows.push({
        icon: 'mdi-cash-multiple',
        color: 'primary',
        label: this.$t('views.BudgetSimulator.flexibleSummaryTotal'),
        value: `${this.formatValue(plan.total)} ${this.spendUnitName}`,
        total: true
      })
      return rows
    },
    // Starts in Free, which the user can switch in the form. The plan's total is the reference budget
    // and each channel's share of it the mix Frozen starts from, so both come from the same spend;
    // the form ignores them when the period has no data (a 0 reference budget falls back to its
    // historical one).
    createFlexibleDates (period) {
      if (!period) return
      const dates = this.flexibleDates(period)
      const plan = this.flexiblePlan(period)
      const base = {
        name: this.$t('views.BudgetSimulator.createFlexibleDates'),
        date_from: dates.dateFrom,
        date_to: dates.dateTo,
        mix_mode: 'free'
      }
      if (plan.total > 0) {
        base.reference_budget = plan.total
        base.mix = normalizeMix(historicalMix(plan.mediaPlan).mix)
      }
      this.openDialog(base)
    },
    cloneSimulation (e) {
      this.openDialog({ ...e.base, name: `${e.base.name} (${this.$t('views.BudgetSimulator.copy')})` })
    },
    editSimulation (e) {
      this.openDialog(e.base, true)
    },
    onCreated (id) {
      this.expandWhenListed(id)
    },
    // Opens a Budget Simulator as soon as the listener brings it in (it may not be listed yet).
    expandWhenListed (id) {
      this.pendingExpand = id
      this.expandPending()
    },
    expandPending () {
      if (!this.pendingExpand) return
      const row = this.budgetSimulationRows.find(item => item.id === this.pendingExpand)
      if (row) {
        if (!this.expanded.some(item => item.id === row.id)) {
          this.expanded.push(row)
        }
        this.pendingExpand = null
      }
    },
    rowClick (item) {
      const index = this.expanded.findIndex(row => row.id === item.id)
      if (index !== -1) {
        this.$delete(this.expanded, index)
      } else {
        this.expanded.push(item)
      }
    },
    gridRange (item) {
      const grid = item.grid || []
      if (grid.length === 0) return ''
      return this.$t('views.BudgetSimulator.gridRange', { from: grid[0].pct, to: grid[grid.length - 1].pct, count: grid.length })
    },
    formatDate (date) {
      return moment(date).format('ll')
    },
    formatDateFromNow (date) {
      return date ? moment(date).fromNow() : ''
    },
    formatValue (value) {
      return numbro(value || 0).format({ average: true, mantissa: 1, optionalMantissa: true })
    }
  },
  computed: {
    ...mapGetters('clients', ['currentClient', 'currentClientObject']),
    ...mapGetters('auth', ['authUser', 'isExternalUser']),
    ...mapGetters('simulations', ['simulationsLoading', 'budgetSimulations', 'budgetSimulationPoints']),
    referenceModelConfig () {
      if (this.currentClientObject && this.currentClientObject.plan_reference_model) {
        return this.currentClientObject.plan_reference_model.config
      } else if (this.currentClientObject && this.currentClientObject.reference_model) {
        return this.currentClientObject.reference_model.config
      }
      return null
    },
    modelFrequency () {
      if (!this.referenceModelConfig) return 'month'
      return this.referenceModelConfig.saturation_granularity === 'W' ? 'week' : 'month'
    },
    spendUnitName () {
      return (this.referenceModelConfig && this.referenceModelConfig.invest_unit_name) || 'USD'
    },
    lastYear () {
      const created = this.currentClientObject && this.currentClientObject.date_created ? this.currentClientObject.date_created.toDate() : null
      return lastYearWindow(this.parsedModelReport, this.modelFrequency, created)
    },
    nextYear () {
      return nextYearWindow(this.parsedModelReport, this.modelFrequency)
    },
    // Dates the form allows, for the "Flexible Dates" calendars (legacy weekly start, no EKRM cap).
    flexibleRange () {
      if (!this.currentClientObject || !this.referenceModelConfig) return null
      return calculatorRange(this.currentClientObject, { ekrmCap: false })
    },
    // Actual spend and budget propagation per period, at the model's frequency.
    flexibleLookup () {
      return buildPrefillLookup(this.parsedModelReport, this.modelFrequency)
    },
    // The part of flexibleRange the report has data for: the dates the "Flexible Dates" calendars allow.
    flexibleBounds () {
      return dataBounds(this.flexibleRange, this.flexibleLookup, this.modelFrequency)
    },
    // Date the "Flexible Dates" calendars open on, in their format (YYYY-MM or YYYY-MM-DD): the
    // start of the Next Year window when there is one, else of the Last Year window. Null when the
    // report has neither actual spend nor propagation at the model's frequency, which hides the template.
    flexibleView () {
      if (!this.flexibleBounds) return null
      const keys = Object.keys(this.flexibleLookup.actual).concat(Object.keys(this.flexibleLookup.propagation))
      if (keys.length === 0) return null
      const weekly = this.modelFrequency === 'week'
      let view = this.nextYear ? this.nextYear.date_from : (this.lastYear ? this.lastYear.date_from : null)
      if (!view) {
        view = keys.sort()[0]
        if (!weekly) view = view + '-01'
      }
      const range = this.flexibleBounds
      view = view < range.start ? range.start : (view > range.end ? range.end : view)
      return weekly ? view : view.slice(0, 7)
    },
    // Where the reference budget's values come from, under the "Flexible Dates" summary.
    flexibleHint () {
      const parts = []
      const actualKeys = Object.keys(this.flexibleLookup.actual).sort()
      const propagationKeys = Object.keys(this.flexibleLookup.propagation).sort()
      if (actualKeys.length > 0) {
        parts.push(this.$t('views.BudgetSimulator.flexibleHintActual', { date: this.formatPeriodKey(actualKeys[actualKeys.length - 1]) }))
      }
      if (propagationKeys.length > 0) {
        parts.push(this.$t('views.BudgetSimulator.flexibleHintPropagation', {
          from: this.formatPeriodKey(propagationKeys[0]),
          to: this.formatPeriodKey(propagationKeys[propagationKeys.length - 1])
        }))
      }
      parts.push(this.$t('views.BudgetSimulator.flexibleHintMix'))
      return parts.join(' ')
    },
    // True while the Storage-hosted model report the templates are built from is being fetched.
    modelReportLoading () {
      return !!this.modelReportFetchKey && this.fetchedModelReportFor !== this.modelReportFetchKey
    },
    // Cards of the "Create" modal; templates without the data they need show disabled, as in the old dropdown.
    createOptions () {
      const options = [{
        id: 'scratch',
        featured: true,
        icon: 'mdi-file-document-edit-outline',
        title: this.$t('views.BudgetSimulator.createFromScratch'),
        description: this.$t('views.BudgetSimulator.chooseFromScratchDescription')
      }]
      if (this.modelReportLoading) return options
      if (this.flexibleView) {
        const weekly = this.modelFrequency === 'week'
        options.push({
          id: 'flexible',
          icon: 'mdi-calendar-range',
          title: this.$t('views.BudgetSimulator.chooseFlexibleTitle'),
          description: this.$t('views.BudgetSimulator.chooseFlexibleDescription'),
          tag: this.$t('views.BudgetSimulator.chooseTagFlexible'),
          period: {
            type: weekly ? 'date' : 'month',
            min: weekly ? this.flexibleBounds.start : this.flexibleBounds.start.slice(0, 7),
            max: weekly ? this.flexibleBounds.end : this.flexibleBounds.end.slice(0, 7),
            // No period comes selected: the calendars only open where the data starts.
            view: this.flexibleView,
            hint: this.flexibleHint,
            summary: this.flexibleSummary
          }
        })
      }
      const needsMediaSpend = this.$t('views.BudgetSimulator.chooseNeedsMediaSpend')
      options.push({
        id: 'lastYearFree',
        icon: 'mdi-history',
        title: this.$t('views.BudgetSimulator.chooseLastYearFreeTitle'),
        description: this.$t('views.BudgetSimulator.chooseLastYearFreeDescription'),
        tag: this.$t('views.BudgetSimulator.chooseTagStrategyCheck'),
        disabled: !this.lastYear,
        disabledReason: needsMediaSpend
      }, {
        id: 'lastYearFrozen',
        icon: 'mdi-snowflake',
        title: this.$t('views.BudgetSimulator.chooseLastYearFrozenTitle'),
        description: this.$t('views.BudgetSimulator.chooseLastYearFrozenDescription'),
        tag: this.$t('views.BudgetSimulator.chooseTagStrategyCheck'),
        disabled: !this.lastYear,
        disabledReason: needsMediaSpend
      }, {
        id: 'nextYearFree',
        icon: 'mdi-calendar-arrow-right',
        title: this.$t('views.BudgetSimulator.chooseNextYearFreeTitle'),
        description: this.$t('views.BudgetSimulator.chooseNextYearFreeDescription'),
        tag: this.$t('views.BudgetSimulator.chooseTagForwardLooking'),
        disabled: !this.nextYear,
        disabledReason: this.$t('views.BudgetSimulator.chooseNeedsBudgetPropagation')
      })
      return options
    },
    channels () {
      if (this.currentClientObject && this.currentClientObject.channels) {
        return this.currentClientObject.channels
      }
      const channels = {}
      if (this.referenceModelConfig && this.referenceModelConfig.media) {
        Object.keys(this.referenceModelConfig.media).forEach(key => {
          channels[key] = { name: key }
        })
      }
      return channels
    },
    ngtorchRunsPlans () {
      const plans = []
      this.budgetSimulations.forEach(simulation => {
        this.budgetSimulationPoints(simulation.id).forEach(point => plans.push({ ...point, clientId: this.currentClient }))
      })
      return plans
    },
    budgetSimulationRows () {
      let rows = this.budgetSimulations.map(simulation => {
        const points = this.budgetSimulationPoints(simulation.id)
        return {
          ...simulation,
          total: (simulation.grid || []).length,
          done: points.filter(point => !!parsedResults(point)).length
        }
      })
      if (this.isExternalUser && this.authUser && this.authUser.uid) {
        const uid = this.authUser.uid
        rows = rows.filter(row => row.created_by_id === uid || row.showToExternalUsers)
      }
      return rows
    },
    headers () {
      return [
        { text: this.$t('views.BudgetSimulator.headerName'), value: 'name', width: '320px' },
        { text: this.$t('views.BudgetSimulator.headerMixMode'), value: 'mix_mode' },
        { text: this.$t('views.BudgetSimulator.headerReferenceBudget', { unit: this.spendUnitName }), value: 'reference_budget' },
        { text: this.$t('views.BudgetSimulator.headerPeriod'), value: 'date_from', width: '220px' },
        { text: this.$t('views.BudgetSimulator.headerProgress'), value: 'progress', sortable: false },
        { text: this.$t('views.BudgetSimulator.headerCreated'), value: 'date_created', width: '135px' }
      ]
    }
  },
  watch: {
    // Older links name the model and the simulation to open in the list.
    '$route.params': {
      immediate: true,
      handler () {
        if (this.$route.params.client) {
          this.setCurrentClient(this.$route.params.client)
        }
        if (this.$route.params.simulation) {
          this.expandWhenListed(this.$route.params.simulation)
        }
      }
    },
    budgetSimulationRows () {
      this.expandPending()
    },
    currentClient: {
      immediate: true,
      handler () {
        if (this.currentClient) {
          this.loadSimulations({ client: this.currentClient })
        }
      }
    }
  }
}
